import { useAppSelector } from 'store/hooks';
import { selectMerchantTimezone } from 'store/settings/settingsSlice';
import React from 'react';
import { useFormikContext } from 'formik';
import { Box, Button, Grid, Typography } from '@mui/material';

import { isDefined } from 'lib/typeguards';
import { LoadingButton } from 'ui/buttons/LoadingButton';
import { Row } from 'ui/Flex';
import { AwardTemplate } from 'components/customers/models/Award';
import { basketQualificationScopeOptions } from 'components/loyalty/enums';
import { EPointOfSaleProvider } from 'components/settings/PointOfSaleSettings';
import { PerkBasketQualificationSection } from './PerkBasketQualificationSection';
import { PerkDateTimeRestrictionSection } from './PerkDateTimeRestrictionSection';
import { PerkDiscountSection } from './PerkDiscountSection';
import { AwardRedemptionFullFormData } from './PerkRedemptionForm';
import { PerkFormsWrapper } from '../PerkFormsWrapper';

interface RedemptionFormCardContentProps {
    onUpdate?: (data: AwardRedemptionFullFormData) => void;
    onPrevious?: (isValid?: boolean, values?: AwardRedemptionFullFormData) => void;
    clickPrevious: () => void;
    isEdit?: boolean;
    isLoading?: boolean;
    currencySymbol: string;
    perkType: string;
    toggledFields?: string[];
    onToggleChange?: (value: string[] | ((state: string[]) => string[])) => void;
    auto?: boolean;
    readOnly?: boolean;
}

export const RedemptionFormCardContent: React.FC<RedemptionFormCardContentProps> = ({
    onUpdate,
    onPrevious,
    isEdit,
    clickPrevious,
    isLoading,
    currencySymbol,
    perkType,
    toggledFields,
    onToggleChange,
    auto,
    readOnly
}) => {
    const { submitForm, values, isValid } = useFormikContext<AwardRedemptionFullFormData>();
    const { settings } = useAppSelector(state => state.settings);

    const posProvider = React.useMemo(() => settings?.posProvider, [settings]);
    const merchantTimezone = useAppSelector(selectMerchantTimezone);
    const isPseudoCurrency = React.useMemo(() => perkType === AwardTemplate.PSEUDO_CURRENCY, [perkType]);

    const handleUpdate = React.useCallback(() => {
        onUpdate(values);
    }, [onUpdate, values]);

    const handlePreviousStep = React.useCallback(() => {
        if (isEdit) {
            onPrevious(isValid, values);
        }
        clickPrevious();
    }, [clickPrevious, isEdit, isValid, onPrevious, values]);

    const isIbs = React.useMemo(() => posProvider === EPointOfSaleProvider.IBS, [posProvider]);

    const isSimphony = React.useMemo(
        () => posProvider === EPointOfSaleProvider.OMNIVORE_MICROS_SIMPHONY,
        [posProvider]
    );

    const isBasketQualificationVisible = React.useMemo(() => {
        if (auto && isIbs) {
            return false;
        }
        if (values.discount?.scope === 'BASKET') {
            return true;
        }
        return [
            values.discount?.scope,
            values.discount?.rule,
            values.discount?.groupedScopeMatchCodes?.length > 0,
            values.discount?.value,
            values.discount?.type
        ].every(isDefined);
    }, [
        auto,
        isIbs,
        values.discount?.rule,
        values.discount?.scope,
        values.discount?.groupedScopeMatchCodes,
        values.discount?.type,
        values.discount?.value
    ]);

    const isDiscountVisible = React.useMemo(() => {
        if (isSimphony) {
            return true;
        }
        return !auto;
    }, [auto, isSimphony]);

    // The date/time section always renders, so its number must follow whichever
    // preceding sections (discount = first, basket qualification = second) are
    // actually visible rather than being hardcoded.
    const dateTimeSectionNumber = React.useMemo(
        () => `4.${(isDiscountVisible ? 1 : 0) + (isBasketQualificationVisible ? 1 : 0) + 1}`,
        [isDiscountVisible, isBasketQualificationVisible]
    );

    const handleSubmit = React.useCallback(() => {
        setTimeout(() => {
            submitForm();
        }, 0);
    }, [submitForm]);

    const discountValueString = React.useMemo(() => {
        if (values.discount?.type === 'OVERRIDE') {
            return `${currencySymbol}${values.discount?.value} override`;
        }
        if (values.discount?.type === 'PERCENTAGE') {
            return `${values.discount?.value}% off`;
        }
        return `${currencySymbol}${values.discount?.value} off`;
    }, [currencySymbol, values.discount?.type, values.discount?.value]);

    return (
        <Grid container spacing={2}>
            <PerkFormsWrapper readOnly={readOnly}>
                <Grid size={12}>
                    <Box padding={3} maxWidth={866}>
                        <Grid container spacing={3}>
                            <Grid size={12}>
                                <Typography variant="h4">4. Redemption</Typography>
                                <Typography variant="body2">
                                    Set up how the award will behave for your users when redeeming through
                                    ordering in your app
                                </Typography>
                            </Grid>
                            {isDiscountVisible && (
                                <PerkDiscountSection
                                    currencySymbol={currencySymbol}
                                    perkType={perkType}
                                    posProvider={posProvider}
                                    isPseudoCurrency={isPseudoCurrency}
                                />
                            )}
                            {isBasketQualificationVisible && (
                                <PerkBasketQualificationSection
                                    currencySymbol={currencySymbol}
                                    isPseudoCurrency={isPseudoCurrency}
                                    discountValueString={discountValueString}
                                    basketScopeOptions={basketQualificationScopeOptions}
                                    toggledFields={toggledFields}
                                    onToggleChange={onToggleChange}
                                />
                            )}
                            <PerkDateTimeRestrictionSection
                                auto={auto}
                                sectionNumber={dateTimeSectionNumber}
                                discountValueString={discountValueString}
                                merchantTimezone={merchantTimezone}
                            />
                        </Grid>
                    </Box>
                </Grid>
            </PerkFormsWrapper>
            <Grid size={12}>
                <Row flex={1} gutter align="flex-end">
                    {isEdit && (
                        <LoadingButton
                            loading={isLoading}
                            disabled={isLoading || !isValid}
                            onClick={handleUpdate}
                            color="primary"
                            variant="outlined"
                        >
                            Update & Close
                        </LoadingButton>
                    )}
                    <Button onClick={handlePreviousStep} color="primary" variant="outlined">
                        Previous
                    </Button>
                    <Button
                        disabled={!isValid || isLoading}
                        onClick={handleSubmit}
                        color="primary"
                        variant="contained"
                    >
                        Next
                    </Button>
                </Row>
            </Grid>
        </Grid>
    );
};
